/* assets/littleg-demo-shell.js — LITTLEG-DEMO-SHELL-RUNTIME-03
 * Owner: Demo Shell install UI, OS instruction rendering, dialog modal & focus restoration.
 *
 * RESPONSIBILITY BOUNDARY (0 Conflict / 0 Overlap Contract)
 * - Canonical PWA lifecycle/install owner: assets/pwa-installer.js via window.GCityPWA.
 * - Demo Shell Markup & Schema: sections/littleg-demo-shell.liquid (LITTLEG-DEMO-SHELL-02)
 * - Demo Shell Visual Styles: assets/littleg-demo-shell.css
 * - Phone Frame Live Embed Sub-owner: snippets/littleg-live-phone-frame.liquid
 * - Primary Map Application: /pages/littleg-map (assets/littleg-kid-map.js)
 * - Family Center & Address Check: assets/littleg-team-account.js & assets/littleg-family-address-check.js
 * - Device Profile Identity: assets/littleg-profile.js (V1.1.0)
 * - Geo Game Bridge: assets/littleg-geo-game-bridge.js (V6)
 *
 * THIS FILE OWNS EXCLUSIVELY:
 * 1. Mobile OS / Browser capability detection for instruction copy only.
 * 2. Localized PWA install instructions rendering.
 * 3. Install <dialog> modal controls with WCAG focus restoration (lastActiveElement).
 * 4. Demo Shell install-button handoff to window.GCityPWA.requestInstall().
 */
(function (window, document) {
  'use strict';

  var VERSION = '2026-10-03-demo-shell-runtime-v3';
  var ROOT_SELECTOR = '[data-littleg-demo]';
  /** @type {HTMLElement | null} */
  let lastActiveElement = null;

  /**
   * @typedef {{status?: string}} InstallResult
   * @typedef {{
   *   requestInstall?: () => InstallResult | void | Promise<InstallResult | void>
   * }} DemoPWA
   */

  const runtimeWindow = /** @type {Window & {
   *   __LITTLEG_DEMO_SHELL_RUNTIME__?: string,
   *   GCityPWA?: DemoPWA
   * }} */ (window);

  /** @type {WeakSet<Element>} */
  const mountedRoots = new WeakSet();

  if (runtimeWindow.__LITTLEG_DEMO_SHELL_RUNTIME__ === VERSION) return;
  runtimeWindow.__LITTLEG_DEMO_SHELL_RUNTIME__ = VERSION;

  /* ========================================================================
     01. HELPERS & DETECTORS
     ======================================================================== */
  /**
   * @param {ParentNode | null} root
   * @param {string} selector
   * @returns {HTMLElement | null}
   */
  function q(root, selector) {
    const element = root ? root.querySelector(selector) : null;
    return element instanceof HTMLElement ? element : null;
  }

  function isStandalone() {
    return (
      (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
      /** @type {Navigator & {standalone?: boolean}} */ (window.navigator).standalone === true
    );
  }

  function isIOS() {
    var ua = window.navigator.userAgent || '';
    return (
      /iPad|iPhone|iPod/.test(ua) ||
      (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1)
    );
  }

  function isAndroid() {
    return /Android/i.test(window.navigator.userAgent || '');
  }

  function isChinese() {
    var lang = document.documentElement.lang || window.navigator.language || '';
    return /^zh/i.test(lang);
  }

  /* ========================================================================
     02. INSTRUCTION RENDERER & DIALOG CONTROLS
     ======================================================================== */
  /** @param {HTMLElement | null} host */
  function setInstructions(host) {
    if (!host) return;

    if (isStandalone()) {
      host.innerHTML = isChinese()
        ? '<p>LittleG 已經以主畫面 App 模式開啟。</p>'
        : '<p>LittleG is already running as a home-screen app.</p>';
      return;
    }

    if (isIOS()) {
      host.innerHTML = isChinese()
        ? [
            '<p><strong>iPhone / iPad：</strong></p>',
            '<p>1. 用 Safari 開啟 LittleG。</p>',
            '<p>2. 點擊 Safari 的「分享」按鈕。</p>',
            '<p>3. 選擇「加入主畫面」。</p>',
          ].join('')
        : [
            '<p><strong>iPhone / iPad:</strong></p>',
            '<p>1. Open LittleG in Safari.</p>',
            '<p>2. Tap the Share button.</p>',
            '<p>3. Choose Add to Home Screen.</p>',
          ].join('');
      return;
    }

    if (isAndroid()) {
      host.innerHTML = isChinese()
        ? [
            '<p><strong>Android：</strong></p>',
            '<p>用 Chrome 開啟 LittleG，再從瀏覽器選單選擇「安裝應用程式」或「加入主畫面」。</p>',
          ].join('')
        : [
            '<p><strong>Android:</strong></p>',
            '<p>Open LittleG in Chrome, then choose Install app or Add to Home screen from the browser menu.</p>',
          ].join('');
      return;
    }

    host.innerHTML = isChinese()
      ? '<p>用手機瀏覽器開啟 LittleG，再使用瀏覽器的「加入主畫面」或「安裝應用程式」功能。</p>'
      : '<p>Open LittleG in your mobile browser and use its Add to Home Screen or Install App option.</p>';
  }

  /**
   * @param {Element} root
   * @param {EventTarget | null} trigger
   */
  function openDialog(root, trigger) {
    const dialog = q(root, '[data-littleg-install-dialog]');
    if (!(dialog instanceof HTMLDialogElement)) return;

    const activeElement = trigger || document.activeElement;
    lastActiveElement = activeElement instanceof HTMLElement ? activeElement : null;
    var instructions = q(dialog, '[data-littleg-install-instructions]');
    setInstructions(instructions);

    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }

    var heading = dialog.querySelector('h2');
    if (heading && !heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
    window.requestAnimationFrame(function () {
      if (heading) heading.focus({ preventScroll: true });
    });
  }

  /** @param {Element} root */
  function closeDialog(root) {
    const dialog = q(root, '[data-littleg-install-dialog]');
    if (!(dialog instanceof HTMLDialogElement)) return;

    if (typeof dialog.close === 'function') {
      if (dialog.open) dialog.close();
    } else {
      dialog.removeAttribute('open');
    }

    if (lastActiveElement && typeof lastActiveElement.focus === 'function') {
      lastActiveElement.focus();
      lastActiveElement = null;
    }
  }

  /* ========================================================================
     03. CANONICAL PWA INSTALL ACTION HANDOFF
     ======================================================================== */
  /**
   * @param {Element} root
   * @param {EventTarget | null} trigger
   */
  function requestInstall(root, trigger) {
    const pwa = runtimeWindow.GCityPWA;
    if (!pwa || typeof pwa.requestInstall !== 'function') {
      openDialog(root, trigger);
      return;
    }

    try {
      Promise.resolve(pwa.requestInstall())
        .then(function (result) {
          if (!result || result.status === 'instructions' || result.status === 'error') {
            openDialog(root, trigger);
          }
        })
        .catch(function () {
          openDialog(root, trigger);
        });
    } catch (_) {
      openDialog(root, trigger);
    }
  }

  /* ========================================================================
     04. MOUNT & LIFECYCLE
     ======================================================================== */
  /** @param {Element | null} root */
  function mount(root) {
    if (!root || mountedRoots.has(root)) return;
    mountedRoots.add(root);

    var installBtn = q(root, '[data-littleg-install]');
    var closeBtn = q(root, '[data-littleg-install-close]');
    var dialog = q(root, '[data-littleg-install-dialog]');

    if (installBtn) {
      installBtn.addEventListener('click', function (event) {
        requestInstall(root, event.currentTarget);
      });
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        closeDialog(root);
      });
    }

    if (dialog) {
      dialog.addEventListener('cancel', function (event) {
        event.preventDefault();
        closeDialog(root);
      });

      dialog.addEventListener('click', function (event) {
        if (event.target === dialog) {
          closeDialog(root);
        }
      });
    }
  }

  function mountAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(mount);
  }

  document.addEventListener('shopify:section:unload', function (event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const section = target.matches(ROOT_SELECTOR) ? target : target.querySelector(ROOT_SELECTOR);

    if (section) mountedRoots.delete(section);
    lastActiveElement = null;
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountAll, { once: true });
  } else {
    mountAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const section = target.matches(ROOT_SELECTOR) ? target : target.querySelector(ROOT_SELECTOR);

    if (section) mount(section);
  });
})(window, document);
